import React from 'react'

import { PREANALYSIS_NOTE_MAX_LENGTH } from '@onr/schema'

import { Button } from '@/components/atoms/Button/Button.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/Dialog/Dialog.tsx'
import { TextArea } from '@/components/atoms/TextArea/TextArea.tsx'

export interface PreanalysisNoteDialogLabels {
  readonly title: string
  readonly placeholder: string
  readonly confirm: string
  readonly cancel: string
}

export interface PreanalysisNoteDialogProps {
  readonly labels: PreanalysisNoteDialogLabels
  readonly initialNote?: string | null
  readonly noteRequired?: boolean
  readonly onConfirm: (note: string) => Promise<void>
  readonly children: React.ReactNode
}

export const PreanalysisNoteDialog = ({
  labels,
  initialNote,
  noteRequired = false,
  onConfirm,
  children,
}: PreanalysisNoteDialogProps) => {
  const [open, setOpen] = React.useState(false)
  const [note, setNote] = React.useState(initialNote ?? '')
  const [working, setWorking] = React.useState(false)

  const confirm = async () => {
    setWorking(true)
    try {
      await onConfirm(note)
      setOpen(false)
    } catch {
      setOpen(true)
    } finally {
      setWorking(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value)
        if (value) {
          setNote(initialNote ?? '')
        }
      }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription className="sr-only">{labels.placeholder}</DialogDescription>
        </DialogHeader>
        <TextArea
          value={note}
          onChange={(event) => {
            setNote(event.target.value)
          }}
          placeholder={labels.placeholder}
          aria-label={labels.placeholder}
          maxLength={PREANALYSIS_NOTE_MAX_LENGTH}
          className="h-[160px] resize-none text-gray-900"
        />
        <DialogFooter>
          <Button
            className="py-1.5"
            disabled={working}
            onClick={() => {
              setOpen(false)
            }}>
            {labels.cancel}
          </Button>
          <Button
            variant="primary"
            className="py-1.5"
            loading={working}
            disabled={noteRequired && note.trim() === ''}
            onClick={confirm}>
            {labels.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
